import { gql } from '@apollo/client';
import { cx } from '@orn/shared-web/utils/cx';
import { Link } from 'react-router-dom';

import { PATHS } from '../../../paths';
import { formatCalendarDate, toCurrency } from '../../../utils/formatting';
import Entity from '../../entity';
import RecordPane from '../../entity/RecordPane';
import { AccountSecondaryElement } from '../../tables/elements';

import { RECENT_TRANSACTIONS_VISIBLE_COUNT } from './recentTransactions';

import type { RecentTransactionFragment } from './__generated__/RecentTransactionsSectionBase.gql';

export const RecentTransactionFragmentDocument = gql`
  fragment RecentTransaction on Transaction {
    id
    amount
    date
    isPending
    financialAccount {
      id
      name
      mask
      bank {
        id
        name
      }
      ...EntityAvatar
    }
  }
  ${Entity.Avatar.Fragment}
`;

export interface IRecentTransactionsSectionBaseProps {
  /** "Recent transactions" at a merchant, "Recent payments" on a stream. */
  title: string;
  /** Already selected: the page's own record dropped, at most `RECENT_TRANSACTIONS_VISIBLE_COUNT`. Empty renders nothing. */
  transactions: RecentTransactionFragment[];
  /** Where "View all" goes; omitted, the header has no link. */
  viewAllPath?: string;
  /** Draws skeleton rows in the card while the read is in flight. */
  loading: boolean;
  className?: string;
}

/**
 * The "Recent Transactions" section of a record page (Figma "Transaction/Card/
 * Recent Transactions"): the newest other transactions at the record's
 * merchant, one row each — date, account chip, amount — linking to that
 * transaction's page. Pure; `RecentTransactionsSection` feeds it from
 * `getTransactionsForUser`.
 */
const RecentTransactionsSectionBase = ({
  title,
  transactions,
  viewAllPath,
  loading,
  className,
}: IRecentTransactionsSectionBaseProps) => {
  if (transactions.length === 0 && !loading) {
    return null;
  }

  return (
    <RecordPane.Section
      title={title}
      link={viewAllPath ? { label: 'View all', to: viewAllPath } : undefined}
      className={className}
    >
      {transactions.length > 0 ? (
        transactions.map((transaction) => <RecentTransactionRow key={transaction.id} transaction={transaction} />)
      ) : (
        <RecentTransactionsSkeleton />
      )}
    </RecordPane.Section>
  );
};

interface IRecentTransactionRowProps {
  transaction: RecentTransactionFragment;
}

const RecentTransactionRow = ({ transaction }: IRecentTransactionRowProps) => {
  const amount = Number(transaction.amount);
  const isCredit = amount < 0;

  return (
    <Link
      to={PATHS.TRANSACTION(transaction.id)}
      className="hover:bg-utility-brand-25 outline-focus-ring flex h-16 items-center gap-4 px-4 transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2"
    >
      <span className="flex min-w-24 shrink basis-60 items-baseline gap-2 overflow-hidden">
        <span className="text-tertiary text-md truncate font-medium leading-[22px]">
          {formatCalendarDate(transaction.date, 'medium')}
        </span>
        {transaction.isPending && <span className="text-tertiary shrink-0 text-xs italic">Pending</span>}
      </span>
      {transaction.financialAccount && (
        <span className="flex shrink-0">
          <AccountSecondaryElement financialAccount={transaction.financialAccount} />
        </span>
      )}
      <span
        className={cx(
          'text-md ml-auto shrink-0 font-semibold leading-[22px]',
          isCredit ? 'text-success-primary' : 'text-primary'
        )}
      >
        {toCurrency(Math.abs(amount), { showSign: isCredit })}
      </span>
    </Link>
  );
};

const SKELETON_ROW_KEYS = Array.from({ length: RECENT_TRANSACTIONS_VISIBLE_COUNT }, (_, index) => index);

const RecentTransactionsSkeleton = () => {
  return (
    <>
      {SKELETON_ROW_KEYS.map((key) => (
        <div
          key={key}
          className="flex h-16 items-center gap-4 px-4"
          aria-busy="true"
          aria-label="Loading recent transactions"
        >
          <div className="bg-tertiary h-4 w-24 animate-pulse rounded-md" />
          <div className="bg-tertiary rounded-xs size-6 animate-pulse" />
          <div className="bg-tertiary h-4 w-14 animate-pulse rounded-md" />
          <div className="bg-tertiary ml-auto h-4 w-16 animate-pulse rounded-md" />
        </div>
      ))}
    </>
  );
};

export default RecentTransactionsSectionBase;
